import { useCallback, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type IntentConfiguration } from '@stripe/stripe-react-native';
import { useInterpret, useSelector } from '@xstate/react';
import * as DateFns from 'date-fns';
import { useNoticeBannersStackContext } from '@sg/garnish';
import { OrderPaymentStatus } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { ignoreTimezone } from '@order/utils';

import { sumLedgerTotal } from '../../components';
import {
  createOrderingMachine,
  type OrderingContext,
} from '../../machines/ordering-machine';
import type {
  AddUpsellToBagParams,
  ApplyGiftCardParams,
  ApplyPromoCodeParams,
  ApplyRewardParams,
  BagReward,
  ChangeLineItemQuantityParams,
  ChangeUtensilsUsageParams,
  DeliveryPreferencesParams,
  OrderingCart,
  OrderingMachineServices,
  RemoveRewardParams,
  SetAvailableExpressPaymentsParams,
  SubmitOrderParams,
  WantedTime,
} from '../../machines/ordering-machine.types';
import {
  adjustWantedTime,
  clearLocalStorageOrderId,
  getOrderPaymentStateConfig,
} from '../../utils';
import { useBagLoyaltyRewardsDeferred } from '../useBagLoyaltyRewardsDeferred';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook to provide an interface to communicate with the Ordering state machine.
 */
export const useOrdering = <LineItem, DeliveryOrderDetail>(
  orderingParams: UseOrderingParams<LineItem, DeliveryOrderDetail>,
) => {
  const {
    isLoggedIn,
    orderSubmissionDelay,
    isIntentConfirmationEnabled,
    wantedTimesPollingInterval,
    bagTracking,
    requestLogin,
    navigatePostOrder,
    fetchInitialData,
    fetchOrderPaymentState,
    fetchStatePostOrder,
    fetchRewards,
    fetchWantedTimes,
    addUpsellToBag,
    updateLineItem,
    changeChannelOrLocation,
    applyReward,
    removeReward,
    applyPromoCode,
    applyGiftCard,
    cancelPreviousIntent,
    submitOrder,
    handlePostOrderPurchaseModal,
    handlePostOrderNotifications,
    handlePostOrderTracking,
    handlePostOrderHistory,
    handlePostOrderAppReview,
  } = orderingParams;

  // ─── Tracking ────────────────────────────────────────────────────────

  const {
    trackBagView,
    trackBagReady,
    trackLocationNamePressed,
    trackWantedTimePressed,
    trackWantedTimeChanged,
    trackDeliveryPreferencesPressed,
    trackDropoffLocationChanged,
    trackLineItemRemoved,
    trackLineItemQuantityChanged,
    trackLoyaltyInfoPressed,
    trackLoyaltyTermsPressed,
    trackApplyRewardPressed,
    trackRemoveRewardPressed,
    trackPromoCodeAddPressed,
    trackUtensilsUsagePressed,
    trackContinuePressed,
    trackChangePaymentMethodPressed,
    trackCreditPressed,
    trackRedeemGiftCardPressed,
    trackRedeemGiftCardSubmitted,
  } = bagTracking;

  // ─── Stripe ──────────────────────────────────────────────────────────

  const stripeConfirmHandlerRef = useRef<
    NonNullable<IntentConfiguration['confirmHandler']>
  >(() => {});

  const registerStripeConfirmHandler = useCallback(
    (handler: NonNullable<IntentConfiguration['confirmHandler']>) => {
      stripeConfirmHandlerRef.current = handler;
    },
    [],
  );

  // ─── State Machine ───────────────────────────────────────────────────

  const isPaymentStatePollingEnabled = useFeatureFlag('CELS-4828-order-payment-state-polling-enabled'); // prettier-ignore
  const orderPaymentStateConfig = useFeatureFlag('CELS-4828-order-payment-state-config'); // prettier-ignore
  const { paymentStatePollingInterval, paymentStateExpirationTolerance, paymentStateDelayedTolerance } = getOrderPaymentStateConfig(orderPaymentStateConfig); // prettier-ignore

  const orderingMachine = useMemo(
    () =>
      createOrderingMachine<LineItem, DeliveryOrderDetail>({
        orderSubmissionDelay,
        wantedTimesPollingInterval,
        isIntentConfirmationEnabled,
        stripeConfirmHandlerRef,
        isPaymentStatePollingEnabled,
        paymentStatePollingInterval,
        paymentStateExpirationTolerance,
        paymentStateDelayedTolerance,
      }),
    [
      isIntentConfirmationEnabled,
      isPaymentStatePollingEnabled,
      orderSubmissionDelay,
      paymentStateExpirationTolerance,
      paymentStateDelayedTolerance,
      paymentStatePollingInterval,
      wantedTimesPollingInterval,
    ],
  );

  // ─── Actions ─────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const onOrderPaymentStateError = useCallback(
    (context: OrderingContext<LineItem, DeliveryOrderDetail>) => {
      const error =
        context.paymentStateError ?? formatMessage(messages.genericError);

      addNoticeBanner({ text: error, palette: 'caution' });
    },
    [addNoticeBanner, formatMessage],
  );

  // ─── Guards ──────────────────────────────────────────────────────────

  const checkIfLoggedIn = useCallback(() => {
    return isLoggedIn;
  }, [isLoggedIn]);

  const checkIfShouldDeferRewardsFetching = useCallback(() => {
    return isLoggedIn;
  }, [isLoggedIn]);

  // ─── Machine Service ─────────────────────────────────────────────────

  const service = useInterpret(orderingMachine, {
    services: {
      fetchInitialData,
      fetchOrderPaymentState,
      fetchStatePostOrder,
      fetchRewards,
      fetchWantedTimes,
      addUpsellToBag,
      updateLineItem,
      applyReward,
      removeReward,
      applyPromoCode,
      applyGiftCard,
      cancelPreviousIntent,
      submitOrder,
    },
    actions: {
      handlePostOrderPurchaseModal,
      handlePostOrderNotifications,
      handlePostOrderTracking,
      handlePostOrderHistory,
      handlePostOrderAppReview,
      clearLocalStorageOrderId,
      onOrderPaymentStateError,
      navigatePostOrder,
      requestLogin,
      changeChannelOrLocation(context) {
        changeChannelOrLocation(context.cart);
      },
      onBagReady: trackBagReady,
    },
    guards: {
      checkIfLoggedIn,
      checkIfShouldDeferRewardsFetching,
    },
  });

  // ─── Context ─────────────────────────────────────────────────────────

  const context = useSelector(service, (currentState) => currentState.context);

  // ─── Total ───────────────────────────────────────────────────────────

  const { total, creditToBeApplied } = useMemo(
    () =>
      sumLedgerTotal(
        context.cart.ledger,
        context.selectedTip,
        context.availableCredit,
        context.shouldUseCredit,
      ),
    [
      context.cart.ledger,
      context.selectedTip,
      context.availableCredit,
      context.shouldUseCredit,
    ],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isFetchingInitialData = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return (
      matches('ordering.idle.waiting') ||
      matches('ordering.loading.fetchingInitialData') ||
      matches('ordering.loading.retrievingInitialWantedTime') ||
      matches('ordering.loading.retrievingInitialOrderPaymentState')
    );
  });

  const isFetchingRewards = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('rewards.fetchingRewards');
  });

  const isFetchingRewardsDeferred = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('rewards.fetchingRewardsDeferred');
  });

  const hasFailedToLoadRewards = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('rewards.error');
  });

  const isChangingLineItemQuantity = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.loading.changingLineItemQuantity');
  });

  const isApplyingPromoCode = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.loading.applyingPromoCode');
  });

  const isChangingTime = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.changingTime');
  });

  const isChangingDeliveryPreferences = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.changingDeliveryPreferences');
  });

  const isCancelingPreviousIntent = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.loading.cancelingPreviousIntent');
  });

  const isSubmittingOrder = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return (
      matches('ordering.loading.confirmingStripe') ||
      matches('ordering.loading.submittingOrder') ||
      matches('ordering.idle.postOrder') ||
      matches('ordering.loading.fetchingStatePostOrder') ||
      matches('orderPaymentState.polling') ||
      matches('orderPaymentState.checking') ||
      matches('orderPaymentState.waiting')
    );
  });

  const isPostOrder = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.postOrder');
  });

  const isPostRedirectDelayed = useSelector(service, (currentState) => {
    const { matches, context: ctx } = currentState;

    const canCancelPaymentIntent =
      ctx.paymentIntentId &&
      ctx.paymentStateStatus === OrderPaymentStatus.Created;

    return matches('postRedirectDelayed.delayed') && canCancelPaymentIntent;
  });

  const isApplyingGiftCard = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.loading.applyingGiftCard');
  });

  const isLineItemQuantityChangePending = useSelector(
    service,
    (currentState) => {
      return currentState.matches('lineItems.pending');
    },
  );

  const isPerformingBagAction = useSelector(service, (currentState) => {
    const { context: ctx, matches } = currentState;
    const isRefetchingRewards =
      ctx.rewards.length > 0 &&
      (matches('rewards.fetchingRewards') ||
        matches('rewards.fetchingRewardsDeferred'));

    return (
      ctx.lineItemIdsBeingUpdated.length > 0 ||
      Boolean(ctx.rewardIdBeingUpdated) ||
      isRefetchingRewards ||
      matches('ordering.loading')
    );
  });

  const isShowingFutureDateWarning = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.showingFutureDateWarning');
  });

  const isShowingRewardWarning = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.showingRewardWarning');
  });

  const isShowingWarning = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return (
      matches('ordering.idle.showingRewardWarning') ||
      matches('ordering.idle.showingFutureDateWarning') ||
      matches('ordering.idle.timeslotAdjustedNotice')
    );
  });

  const isShowingCheckoutLedger = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.checkoutLedger');
  });

  const isShowingOrderPlayback = useSelector(service, (currentState) => {
    const { context, matches } = currentState;

    return (
      context.hasTriggeredOrderSubmission &&
      (matches('ordering.idle.orderPlayback') ||
        matches('ordering.loading.confirmingStripe') ||
        matches('ordering.loading.submittingOrder') ||
        matches('ordering.idle.postOrder') ||
        matches('ordering.loading.fetchingStatePostOrder') ||
        matches('orderPaymentState.polling') ||
        matches('orderPaymentState.checking') ||
        matches('orderPaymentState.waiting'))
    );
  });

  const isShowingTimeslotAdjustedNotice = useSelector(
    service,
    (currentState) => {
      const { matches } = currentState;

      return matches('ordering.idle.timeslotAdjustedNotice');
    },
  );

  const isShowingNextDayOrderNotice = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.nextDayOrderNotice');
  });

  const isChangingPaymentMethod = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.changingPaymentMethod');
  });

  const isShowingGiftCardForm = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return (
      matches('ordering.loading.applyingGiftCard') ||
      matches('ordering.idle.giftCardForm')
    );
  });

  const isShowingLoyaltyInfo = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.loyaltyInfo');
  });

  const isShowingLoyaltyTerms = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ordering.idle.loyaltyTerms');
  });

  const isShowingModal =
    isChangingTime ||
    isChangingDeliveryPreferences ||
    isShowingCheckoutLedger ||
    isChangingPaymentMethod ||
    isShowingGiftCardForm ||
    isShowingLoyaltyInfo ||
    isShowingLoyaltyTerms ||
    isApplyingGiftCard ||
    isShowingNextDayOrderNotice ||
    isShowingOrderPlayback;

  const hasLineItems = (context?.cart?.lineItems?.length ?? 0) > 0;
  const isBagEmpty = !isFetchingInitialData && !hasLineItems && !isShowingModal;
  const isBagValid = !isFetchingInitialData && hasLineItems && !isPostOrder;

  // ─── Machine Helpers ─────────────────────────────────────────────────

  const startOrdering = useCallback(() => {
    service.send('START');
    trackBagView();
  }, [service, trackBagView]);

  const startPostRedirectDelay = useCallback(() => {
    service.send('START_POST_REDIRECT_DELAY');
  }, [service]);

  const cancelRequest = useCallback(
    (overrideCheck = false) => {
      // Prevents cancelling requests (closing modals) during order submission.
      // This check will be overwritten if the submission fails and needs to be canceled.
      if (isSubmittingOrder && !overrideCheck) return;

      service.send('CANCEL_REQUEST');
    },
    [service, isSubmittingOrder],
  );

  const cancelPreviousIntentAction = useCallback(() => {
    service.send('CANCEL_PREVIOUS_INTENT');
  }, [service]);

  const setConfirmStripe = useCallback(
    (confirmStripe: () => void) => {
      service.send('SET_CONFIRM_STRIPE', { params: { confirmStripe } });
    },
    [service],
  );

  const requestChannelOrLocationChange = useCallback(() => {
    service.send('REQUEST_CHANNEL_LOCATION_CHANGE');
    trackLocationNamePressed();
  }, [service, trackLocationNamePressed]);

  const requestTimeChange = useCallback(() => {
    service.send('REQUEST_TIME_CHANGE');
    trackWantedTimePressed();
  }, [service, trackWantedTimePressed]);

  const changeTime = useCallback(
    (wantedTime: WantedTime) => {
      const adjustedTime = adjustWantedTime(wantedTime);
      const validTime = adjustedTime ? ignoreTimezone(adjustedTime) : null;
      const isFutureDate = validTime ? DateFns.isTomorrow(validTime) : false;
      service.send('CHANGE_TIME', { params: { wantedTime, isFutureDate } });
      trackWantedTimeChanged();
    },
    [service, trackWantedTimeChanged],
  );

  const refetchWantedTimes = useCallback(() => {
    service.send('REFETCH_WANTED_TIMES');
  }, [service]);

  const changeTip = useCallback(
    (tip: number) => {
      service.send('CHANGE_TIP', { params: { tip } });
    },
    [service],
  );

  const requestDeliveryPreferencesChange = useCallback(() => {
    service.send('REQUEST_DELIVERY_PREFERENCES_CHANGE');
    trackDeliveryPreferencesPressed();
  }, [service, trackDeliveryPreferencesPressed]);

  const changeDeliveryPreferences = useCallback(
    (params: DeliveryPreferencesParams['params']) => {
      service.send('CHANGE_DELIVERY_PREFERENCES', { params });
    },
    [service],
  );

  const changeDropoffLocation = useCallback(
    (dropoffLocation: string) => {
      service.send('CHANGE_DROPOFF_LOCATION', { params: { dropoffLocation } });
      trackDropoffLocationChanged();
    },
    [service, trackDropoffLocationChanged],
  );

  const addUpsell = useCallback(
    (params: AddUpsellToBagParams['params']) => {
      service.send('ADD_UPSELL_TO_BAG', { params });
    },
    [service],
  );

  const startChangingLineItemQuantity = useCallback(() => {
    service.send('START_CHANGING_LINE_ITEM_QUANTITY');
  }, [service]);

  const stopChangingLineItemQuantity = useCallback(() => {
    service.send('STOP_CHANGING_LINE_ITEM_QUANTITY');
  }, [service]);

  const changeLineItemQuantity = useCallback(
    (params: ChangeLineItemQuantityParams['params']) => {
      service.send('CHANGE_LINE_ITEM_QUANTITY', { params });
      params.quantity > 0
        ? trackLineItemQuantityChanged()
        : trackLineItemRemoved();
    },
    [service, trackLineItemQuantityChanged, trackLineItemRemoved],
  );

  const changeUtensilsUsage = useCallback(
    (params: ChangeUtensilsUsageParams['params']) => {
      service.send('CHANGE_UTENSILS_USAGE', { params });
      trackUtensilsUsagePressed();
    },
    [service, trackUtensilsUsagePressed],
  );

  const showLoyaltyInfo = useCallback(() => {
    service.send('SHOW_LOYALTY_INFO');
    trackLoyaltyInfoPressed();
  }, [service, trackLoyaltyInfoPressed]);

  const showLoyaltyTerms = useCallback(
    (rewardId: string) => {
      service.send('SHOW_LOYALTY_TERMS', { params: { rewardId } });
      trackLoyaltyTermsPressed();
    },
    [service, trackLoyaltyTermsPressed],
  );

  const applyBagReward = useCallback(
    (rewardId: string) => {
      service.send('APPLY_REWARD', {
        params: { rewardId, orderId: context.cart.orderId },
      });
      trackApplyRewardPressed();
    },
    [context.cart.orderId, service, trackApplyRewardPressed],
  );

  const removeBagReward = useCallback(
    (rewardId: string) => {
      service.send('REMOVE_REWARD', {
        params: { rewardId, orderId: context.cart.orderId },
      });
      trackRemoveRewardPressed();
    },
    [context.cart.orderId, service, trackRemoveRewardPressed],
  );

  const refetchRewards = useCallback(() => {
    service.send('REFETCH_REWARDS');
  }, [service]);

  const applyBagPromoCode = useCallback(
    (code: string) => {
      service.send('APPLY_PROMO_CODE', { params: { code } });
      trackPromoCodeAddPressed();
    },
    [service, trackPromoCodeAddPressed],
  );

  const requestCheckoutLedger = useCallback(() => {
    service.send('REQUEST_CHECKOUT_LEDGER');
    trackContinuePressed();
  }, [service, trackContinuePressed]);

  const requestOrderPlayback = useCallback(
    (paymentMethodId?: string) => {
      service.send('SHOW_ORDER_PLAYBACK', { params: { paymentMethodId } });
      trackContinuePressed();
    },
    [service, trackContinuePressed],
  );

  const requestPaymentMethodChange = useCallback(() => {
    service.send('REQUEST_PAYMENT_METHOD_CHANGE');
    trackChangePaymentMethodPressed();
  }, [service, trackChangePaymentMethodPressed]);

  const changePaymentMethod = useCallback(
    (paymentMethodId: string) => {
      service.send('CHANGE_PAYMENT_METHOD', { params: { paymentMethodId } });
    },
    [service],
  );

  const finishPaymentSelection = useCallback(() => {
    service.send('FINISH_PAYMENT_SELECTION');
  }, [service]);

  const changeCreditUsage = useCallback(
    (shouldUseCredit: boolean) => {
      service.send('CHANGE_CREDIT_USAGE', { params: { shouldUseCredit } });
      trackCreditPressed();
    },
    [service, trackCreditPressed],
  );

  const requestGiftCardForm = useCallback(() => {
    service.send('REQUEST_GIFT_CARD_FORM');
    trackRedeemGiftCardPressed();
  }, [service, trackRedeemGiftCardPressed]);

  const submitGiftCardForm = useCallback(
    (code: string, regCode: string) => {
      service.send('APPLY_GIFT_CARD', {
        params: {
          code,
          regCode,
        },
      });
      trackRedeemGiftCardSubmitted();
    },
    [service, trackRedeemGiftCardSubmitted],
  );

  const setAvailableExpressPayments = useCallback(
    (params: SetAvailableExpressPaymentsParams['params']) => {
      service.send('SET_AVAILABLE_EXPRESS_PAYMENTS', { params });
    },
    [service],
  );

  // The stripe confirmation is triggered by the Stripe SDK.
  // It takes a few seconds so we need it to be in the machine to show loading states.
  const startStripeConfirmation = useCallback(() => {
    if (!isIntentConfirmationEnabled) return;
    service.send('START_STRIPE_CONFIRMATION');
  }, [isIntentConfirmationEnabled, service]);

  const requestOrderSubmission = useCallback(
    (paymentMethodId?: string) => {
      service.send('SUBMIT_ORDER', { params: { paymentMethodId } });
    },
    [service],
  );

  const assignRewards = useCallback(
    (rewards: BagReward[]) => {
      service.send('ASSIGN_REWARDS', { data: { rewards } });
    },
    [service],
  );

  const onRewardsFetchFailure = useCallback(() => {
    service.send('REWARDS_FETCH_ERROR');
  }, [service]);

  // ─── Deferred helpers ────────────────────────────────────────────────

  useBagLoyaltyRewardsDeferred({
    shouldFetchRewards: isFetchingRewardsDeferred,
    onSuccess: assignRewards,
    onFailure: onRewardsFetchFailure,
  });

  // ─── Back Behavior ───────────────────────────────────────────────────

  const canGoBack = isChangingPaymentMethod || isShowingGiftCardForm;

  const backBehavior = useCallback(() => {
    if (isChangingPaymentMethod) {
      service.send('REQUEST_CHECKOUT_LEDGER');
    }

    if (isShowingGiftCardForm) {
      requestPaymentMethodChange();
    }
  }, [
    isChangingPaymentMethod,
    isShowingGiftCardForm,
    service,
    requestPaymentMethodChange,
  ]);

  const goBack = canGoBack ? backBehavior : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      ...context,
      total,
      creditToBeApplied,
      bagTracking,
      isFetchingInitialData,
      isFetchingRewards: isFetchingRewards || isFetchingRewardsDeferred,
      hasFailedToLoadRewards,
      isBagEmpty,
      isBagValid,
      isChangingTime,
      isChangingDeliveryPreferences,
      isChangingLineItemQuantity,
      isApplyingPromoCode,
      isShowingFutureDateWarning,
      isShowingRewardWarning,
      isCancelingPreviousIntent,
      isSubmittingOrder,
      isPostOrder,
      isPostRedirectDelayed,
      isApplyingGiftCard,
      isLineItemQuantityChangePending,
      isPerformingBagAction,
      isShowingWarning,
      isShowingCheckoutLedger,
      isShowingOrderPlayback,
      isShowingTimeslotAdjustedNotice,
      isShowingNextDayOrderNotice,
      isChangingPaymentMethod,
      isShowingGiftCardForm,
      isShowingLoyaltyInfo,
      isShowingLoyaltyTerms,
      isShowingModal,
      registerStripeConfirmHandler,
      startOrdering,
      startPostRedirectDelay,
      cancelRequest,
      cancelPreviousIntent: cancelPreviousIntentAction,
      setConfirmStripe,
      requestChannelOrLocationChange,
      requestTimeChange,
      changeTime,
      refetchWantedTimes,
      changeTip,
      requestDeliveryPreferencesChange,
      addUpsell,
      changeDeliveryPreferences,
      changeDropoffLocation,
      startChangingLineItemQuantity,
      stopChangingLineItemQuantity,
      changeLineItemQuantity,
      changeUtensilsUsage,
      showLoyaltyInfo,
      showLoyaltyTerms,
      applyBagReward,
      removeBagReward,
      refetchRewards,
      applyBagPromoCode,
      requestOrderPlayback,
      requestCheckoutLedger,
      requestPaymentMethodChange,
      requestGiftCardForm,
      submitGiftCardForm,
      changeCreditUsage,
      changePaymentMethod,
      finishPaymentSelection,
      setAvailableExpressPayments,
      startStripeConfirmation,
      requestOrderSubmission,
      goBack,
    }),
    [
      context,
      total,
      creditToBeApplied,
      bagTracking,
      isFetchingInitialData,
      isFetchingRewards,
      isFetchingRewardsDeferred,
      hasFailedToLoadRewards,
      isBagEmpty,
      isBagValid,
      isChangingTime,
      isChangingDeliveryPreferences,
      isChangingLineItemQuantity,
      isApplyingPromoCode,
      isShowingFutureDateWarning,
      isShowingRewardWarning,
      isCancelingPreviousIntent,
      isSubmittingOrder,
      isPostOrder,
      isPostRedirectDelayed,
      isApplyingGiftCard,
      isLineItemQuantityChangePending,
      isPerformingBagAction,
      isShowingWarning,
      isShowingCheckoutLedger,
      isShowingOrderPlayback,
      isShowingTimeslotAdjustedNotice,
      isShowingNextDayOrderNotice,
      isChangingPaymentMethod,
      isShowingGiftCardForm,
      isShowingLoyaltyInfo,
      isShowingLoyaltyTerms,
      isShowingModal,
      registerStripeConfirmHandler,
      startOrdering,
      startPostRedirectDelay,
      cancelRequest,
      cancelPreviousIntentAction,
      setConfirmStripe,
      requestChannelOrLocationChange,
      requestTimeChange,
      changeTime,
      refetchWantedTimes,
      changeTip,
      requestDeliveryPreferencesChange,
      addUpsell,
      changeDeliveryPreferences,
      changeDropoffLocation,
      startChangingLineItemQuantity,
      stopChangingLineItemQuantity,
      changeLineItemQuantity,
      changeUtensilsUsage,
      showLoyaltyInfo,
      showLoyaltyTerms,
      applyBagReward,
      removeBagReward,
      refetchRewards,
      applyBagPromoCode,
      requestOrderPlayback,
      requestCheckoutLedger,
      requestPaymentMethodChange,
      requestGiftCardForm,
      submitGiftCardForm,
      changeCreditUsage,
      changePaymentMethod,
      finishPaymentSelection,
      setAvailableExpressPayments,
      startStripeConfirmation,
      requestOrderSubmission,
      goBack,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOrderingParams<LineItem, DeliveryOrderDetail> = {
  isLoggedIn: boolean;
  orderSubmissionDelay: number;
  isIntentConfirmationEnabled: boolean;
  wantedTimesPollingInterval: number;
  bagTracking: {
    trackBagView: () => void;
    trackBagReady: (
      context: OrderingContext<LineItem, DeliveryOrderDetail>,
    ) => void;
    trackLocationNamePressed: () => void;
    trackWantedTimePressed: () => void;
    trackWantedTimeChanged: () => void;
    trackDeliveryPreferencesPressed: () => void;
    trackDropoffLocationChanged: () => void;
    trackViewMenuPressed: () => void;
    trackLineItemPressed: () => void;
    trackLineItemRemoved: () => void;
    trackLineItemQuantityChanged: () => void;
    trackLoyaltyInfoPressed: () => void;
    trackLoyaltyTermsPressed: () => void;
    trackLoyaltySignedOutPressed: () => void;
    trackApplyRewardPressed: () => void;
    trackRemoveRewardPressed: () => void;
    trackPromoCodeAddPressed: () => void;
    trackUtensilsUsagePressed: () => void;
    trackContinuePressed: () => void;
    trackChangePaymentMethodPressed: () => void;
    trackCreditPressed: () => void;
    trackPaymentMethodPressed: () => void;
    trackRedeemGiftCardPressed: () => void;
    trackRedeemGiftCardSubmitted: () => void;
  };
  handlePostOrderPurchaseModal: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
  ) => void;
  handlePostOrderNotifications: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
  ) => void;
  handlePostOrderTracking: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
  ) => void;
  handlePostOrderHistory: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
  ) => void;
  handlePostOrderAppReview: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
  ) => void;
  navigatePostOrder: () => void;
  changeChannelOrLocation: (
    params: OrderingCart<LineItem, DeliveryOrderDetail>,
  ) => void;
  requestLogin: () => void;
  fetchInitialData: () => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['fetchInitialData']['data']
  >;
  fetchOrderPaymentState: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['fetchOrderPaymentState']['data']
  >;
  fetchStatePostOrder: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['fetchStatePostOrder']['data']
  >;
  fetchRewards: () => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['fetchRewards']['data']
  >;
  fetchWantedTimes: () => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['fetchWantedTimes']['data']
  >;
  addUpsellToBag: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
    params: AddUpsellToBagParams,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['addUpsellToBag']['data']
  >;
  updateLineItem: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
    params: ChangeLineItemQuantityParams,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['updateLineItem']['data']
  >;
  applyReward: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
    params: ApplyRewardParams,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['applyReward']['data']
  >;
  removeReward: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
    params: RemoveRewardParams,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['removeReward']['data']
  >;
  applyPromoCode: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
    params: ApplyPromoCodeParams,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['applyPromoCode']['data']
  >;
  applyGiftCard: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
    params: ApplyGiftCardParams,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['applyGiftCard']['data']
  >;
  cancelPreviousIntent: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['cancelPreviousIntent']['data']
  >;
  submitOrder: (
    context: OrderingContext<LineItem, DeliveryOrderDetail>,
    params: SubmitOrderParams,
  ) => Promise<
    OrderingMachineServices<
      LineItem,
      DeliveryOrderDetail
    >['submitOrder']['data']
  >;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  genericError: {
    defaultMessage:
      'There was an error processing your order. Please try again.',
    description: 'Ordering | Order payment state error',
  },
});
